import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import BlissBasicLogo from '../components/bliss/BlissBasicLogo'
import {
  BLISS_BASIC_HERO_BODY,
  BLISS_BASIC_HERO_TITLE,
  BLISS_BASIC_HERO_TITLE_LINES,
} from '../config/blissBasic'
import { BLISS_BASIC_BACK_OFFICE_PATH, BLISS_CUSTOM_FUNNEL_URL } from '../config/blissBasicHost'
import { blissDesignStudioFreshEntryUrl } from '../config/blissDesignHost'
import { fetchBlissBasicPublicCatalog } from '../services/blissBasicCatalogClient'
import { trackBlissFunnelEvent } from '../services/funnelAnalytics'
import heroBunkPhoto from '../assets/slide2.png'
import './BlissBasicSite.css'
import PublishedSupplierCollection from '../components/bliss/PublishedSupplierCollection'

const CONTACT_EMAIL = 'hello@blissbunkbeds.com'

function splitPrefillName(name: string): { firstName: string; lastName: string } {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return { firstName: '', lastName: '' }
  if (parts.length === 1) return { firstName: parts[0], lastName: '' }
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') }
}

function readPrefillFromUrl() {
  if (typeof window === 'undefined') {
    return { firstName: '', lastName: '', email: '', projectZip: '', linkedQuoteRequestId: undefined as string | undefined }
  }
  const q = new URLSearchParams(window.location.search)
  const name = q.get('name') || ''
  const parts = splitPrefillName(name)
  return {
    firstName: parts.firstName,
    lastName: parts.lastName,
    email: q.get('email')?.trim() || '',
    projectZip: q.get('zip')?.trim() || '',
    linkedQuoteRequestId: q.get('bliss_quote_id')?.trim() || undefined,
  }
}

export default function BlissBasicSite() {
  const location = useLocation()
  const { user } = useAuth()
  const prefill = useMemo(() => readPrefillFromUrl(), [])
  const handoffStarted = useRef(false)
  const [heroUrl, setHeroUrl] = useState<string | null>(null)


  useEffect(() => {
    void fetchBlissBasicPublicCatalog().then((catalog) => {
      if (!catalog) return
      const hero = catalog.heroPhoto?.url
      if (hero) setHeroUrl(hero)
    })
  }, [])

  useEffect(() => {
    trackBlissFunnelEvent('bliss_basic_site_view')
  }, [])

  /** Prefill handoff tracking when quote funnel passes contact in the URL. */
  useEffect(() => {
    if (handoffStarted.current) return
    const email = prefill.email
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return
    if (!prefill.firstName.trim() && !prefill.lastName.trim()) return
    const zip = prefill.projectZip.trim()
    if (!/^\d{5}$/.test(zip)) return
    handoffStarted.current = true
    trackBlissFunnelEvent('bliss_basic_prefill_handoff')
  }, [prefill])

  return (
    <div className="bliss-basic-page">
        <Link
          className="bliss-basic-signin"
          to={user ? BLISS_BASIC_BACK_OFFICE_PATH : '/login'}
          state={user ? undefined : { from: { pathname: BLISS_BASIC_BACK_OFFICE_PATH } }}
        >
          {user ? 'Dashboard' : 'Log in'}
        </Link>

      <section className="bliss-basic-hero">
        <div className="bliss-basic-hero-copy">
          <div className="bliss-basic-hero-stack">
            <BlissBasicLogo variant="hero" className="bliss-basic-hero-logo" />

            <div className="bliss-basic-hero-inner">
            <h1 className="bliss-basic-hero-title">
              <span className="bliss-basic-hero-title-primary">{BLISS_BASIC_HERO_TITLE_LINES.primary}</span>
              <span className="bliss-basic-hero-title-secondary">{BLISS_BASIC_HERO_TITLE_LINES.secondary}</span>
              <span className="visually-hidden">{BLISS_BASIC_HERO_TITLE}</span>
            </h1>

            <p className="bliss-basic-hero-body">{BLISS_BASIC_HERO_BODY}</p>

            <div className="bliss-basic-investment-box">
              <a className="bliss-basic-investment-value" href="tel:+18508120704">Call for Price</a>
              <span className="bliss-basic-investment-note">A brief verification call, then your quote.</span>
            </div>

            <a
              className="bliss-basic-primary-btn"
              href={blissDesignStudioFreshEntryUrl(location.search)}
              onClick={() => trackBlissFunnelEvent('bliss_basic_design_studio_cta_click')}
            >
              Bunkroom Design Studio →
            </a>
            <a className="bliss-basic-secondary-link" href={BLISS_CUSTOM_FUNNEL_URL}>
              Compare fully custom Bliss
            </a>
            </div>
          </div>
        </div>

        <div className="bliss-basic-hero-visual">
          <img
            src={heroUrl ?? heroBunkPhoto}
            alt="Built-in bunk room with warm lighting"
            className="bliss-basic-hero-img"
          />
        </div>
      </section>

      <PublishedSupplierCollection />
      <footer className="bliss-basic-footer">
        <p>
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          {' · '}
          <a href={BLISS_CUSTOM_FUNNEL_URL}>Fully custom rooms</a>
        </p>
      </footer>
    </div>
  )
}
